import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { API } from "../App";
import Layout from "../components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ClipboardText, DownloadSimple, MagnifyingGlass } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useMonth } from "../contexts/MonthContext";
import { exportWithHeader } from "../utils/excelExport";

const f2 = (v) => typeof v === "number"
  ? v.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")
  : "0.00";

const DR_PREFS_KEY = "daily_review_prefs_v1";

// What we ship: rm-code-wise Required vs Stock vs WIP vs Balance procurement.
// Window mirrors the Daily Review Sheet (date_from / date_to) when set, else
// falls back to the global Month/Year period. This is the same logic the
// dashboard insights use so users see one unified "window" everywhere.
export default function RMPlanning() {
  const { period } = useMonth();
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState(null);
  const [windowLabel, setWindowLabel] = useState("");
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  // Till-Month filter. When set, RM Planning aggregates every sales-register
  // schedule with `delivery_date <= end of that month`. Overrides both the
  // Daily Review Sheet window and the global Month/Year period so the user
  // sees the cumulative procurement need up to that horizon.
  const [tillMonth, setTillMonth] = useState(""); // "YYYY-MM"

  const readDR = () => {
    try {
      const v = JSON.parse(localStorage.getItem(DR_PREFS_KEY) || "{}");
      return { date_from: v.date_from || "", date_to: v.date_to || "" };
    } catch { return { date_from: "", date_to: "" }; }
  };
  const [dr, setDr] = useState(readDR);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (tillMonth) {
        // Cumulative "till end of month" — pass date_to as the last day of
        // that month, leave date_from open so we sweep every earlier order.
        const [y, m] = tillMonth.split("-").map(Number);
        const lastDay = new Date(y, m, 0).getDate();  // JS: month is 1-based here
        params.date_to = `${tillMonth}-${String(lastDay).padStart(2, "0")}`;
      } else if (dr.date_from || dr.date_to) {
        if (dr.date_from) params.date_from = dr.date_from;
        if (dr.date_to) params.date_to = dr.date_to;
      } else {
        params.month = period.month;
        params.year = period.year;
      }
      const res = await axios.get(`${API}/rm/planning`, { params });
      setRows(res.data.rows || []);
      setTotals(res.data.totals || null);
      setWindowLabel(res.data.window || "");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load RM Planning");
    } finally { setLoading(false); }
  }, [dr.date_from, dr.date_to, period.month, period.year, tillMonth]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Re-read DRS window on tab focus / cross-tab storage events so changing
  // the duration in Daily Review Sheet flows over here without a reload.
  useEffect(() => {
    const sync = () => setDr(readDR());
    window.addEventListener && window.addEventListener("focus", sync);
    window.addEventListener && window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener && window.removeEventListener("focus", sync);
      window.removeEventListener && window.removeEventListener("storage", sync);
    };
  }, []);

  const filtered = useMemo(() => {
    const s = search.trim().toUpperCase();
    if (!s) return rows;
    return rows.filter(r =>
      (r.part_no || "").toUpperCase().includes(s) ||
      (r.part_name || "").toUpperCase().includes(s) ||
      (r.customer || "").toUpperCase().includes(s) ||
      (r.rm_code || "").toUpperCase().includes(s) ||
      (r.rm_type || "").toUpperCase().includes(s) ||
      (r.rm_grade || "").toUpperCase().includes(s)
    );
  }, [rows, search]);

  // Live totals — recomputed from the currently-filtered rows so the
  // "Required / In Stock / In WIP / Balance Procurement" tiles reflect
  // whatever the user is filtering on (e.g. a single grade). Falls back
  // to backend-provided `totals` when there's no active search.
  const liveTotals = useMemo(() => {
    if (!filtered.length) return totals;
    if (!search.trim()) return totals;
    const acc = { required_qty: 0, required_value: 0, stock_qty: 0, stock_value: 0,
                  wip_qty: 0, wip_value: 0, bought_qty: 0, bought_value: 0,
                  balance_qty: 0, balance_value: 0 };
    for (const r of filtered) {
      for (const k of Object.keys(acc)) acc[k] += Number(r[k] || 0);
    }
    return acc;
  }, [filtered, search, totals]);

  const handleExport = () => {
    const data = filtered.map(r => ({
      "Sr No": r.sr_no,
      "Part No": r.part_no,
      "Part Name": r.part_name,
      "Customer": r.customer,
      "RM Code": r.rm_code,
      "RM Type": r.rm_type,
      "RM Grade": r.rm_grade,
      "Gross Wt (Kg)": r.gross_weight,
      "Avg Rate (₹/Kg)": r.avg_rate,
      "Required Quantity (Kg)": r.required_qty,
      "Required Value (₹)": r.required_value,
      "RM Stock (Kg)": r.stock_qty,
      "RM Stock Value (₹)": r.stock_value,
      "WIP (Kg)": r.wip_qty,
      "WIP Value (₹)": r.wip_value,
      "RM Bought (Kg)": r.bought_qty,
      "RM Bought Value (₹)": r.bought_value,
      "Balance Procurement Quantity (Kg)": r.balance_qty,
      "Balance Procurement Value (₹)": r.balance_value,
    }));
    exportWithHeader(
      [{ name: "RM Planning", data }],
      `RM-Planning-${windowLabel || `${period.year}-${String(period.month).padStart(2, '0')}`}`,
      `RM Planning · Window: ${windowLabel || `${period.year}-${String(period.month).padStart(2, '0')}`}`,
    );
  };

  return (
    <Layout>
      <div className="p-3 sm:p-5 space-y-4" data-testid="rm-planning-page">
        {/* Header */}
        <Card className="border border-[#E2E8F0] p-4">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <h1 className="text-2xl font-black tracking-tight text-[#0F172A] flex items-center gap-2" style={{ fontFamily: 'Chivo' }}>
                <ClipboardText size={24} weight="bold" /> RM Planning
              </h1>
              <p className="text-xs text-[#64748B] mt-1">
                Code-wise Required → Stock → WIP → Balance procurement. Window:&nbsp;
                <span className="font-mono font-bold text-[#1E40AF]">{windowLabel || "current period"}</span>
                {tillMonth ? (
                  <span className="ml-2 text-[10px] font-mono px-2 py-0.5 rounded-full bg-[#F5F3FF] text-[#7C3AED] border border-[#DDD6FE]">
                    cumulative till {tillMonth}
                  </span>
                ) : (dr.date_from || dr.date_to) && (
                  <span className="ml-2 text-[10px] font-mono px-2 py-0.5 rounded-full bg-[#EFF6FF] text-[#1E40AF] border border-[#BFDBFE]"
                    title="Window comes from the Daily Review Sheet date filter. Change it there to refilter this report.">
                    via Daily Review Sheet
                  </span>
                )}
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex items-center gap-1.5 border border-[#E2E8F0] rounded px-2 h-9 bg-white"
                title="Cumulative schedule up to the end of this month. Overrides the Daily Review window.">
                <span className="text-[10px] font-bold uppercase tracking-wider text-[#7C3AED]">Till</span>
                <Input
                  type="month"
                  value={tillMonth}
                  onChange={e => setTillMonth(e.target.value)}
                  className="h-7 w-40 border-none focus-visible:ring-0 p-0 text-xs font-mono"
                  data-testid="rm-planning-till-month"
                />
                {tillMonth && (
                  <button onClick={() => setTillMonth("")}
                    className="text-[10px] text-[#94A3B8] hover:text-[#DC2626] px-1"
                    data-testid="rm-planning-till-month-clear"
                    title="Clear till-month filter">×</button>
                )}
              </div>
              <div className="relative">
                <MagnifyingGlass size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
                <Input
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search RM code / part / grade"
                  className="h-9 pl-7 w-64 text-sm"
                  data-testid="rm-planning-search"
                />
              </div>
              <Button onClick={handleExport} variant="outline" className="h-9 text-xs" data-testid="rm-planning-export">
                <DownloadSimple size={14} className="mr-1.5" /> Export Excel
              </Button>
              <Button onClick={fetchData} variant="outline" className="h-9 text-xs" data-testid="rm-planning-refresh">
                Refresh
              </Button>
            </div>
          </div>
        </Card>

        {/* Totals tiles — two rows per tile: Value (₹) top, KG bottom */}
        {liveTotals && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <TotalTile label="Total RM Required" qty={liveTotals.required_qty} value={liveTotals.required_value} color="#1D2951" />
            <TotalTile label="RM in Stock" qty={liveTotals.stock_qty} value={liveTotals.stock_value} color="#0F766E" />
            <TotalTile label="WIP Stock" qty={liveTotals.wip_qty} value={liveTotals.wip_value} color="#B45309" />
            <TotalTile label="Balance Procurement" qty={liveTotals.balance_qty} value={liveTotals.balance_value} color="#DC2626" highlight />
          </div>
        )}

        {/* Table */}
        <Card className="border border-[#E2E8F0] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="rm-planning-table">
              <thead className="bg-[#1D2951] text-white sticky top-0">
                <tr>
                  <th className="px-2 py-2 text-center font-bold w-12">#</th>
                  <th className="px-3 py-2 text-left font-bold">Part No</th>
                  <th className="px-3 py-2 text-left font-bold">Part Name</th>
                  <th className="px-3 py-2 text-left font-bold">Customer</th>
                  <th className="px-3 py-2 text-left font-bold">RM Code</th>
                  <th className="px-3 py-2 text-right font-bold">Gross Weight</th>
                  <th className="px-3 py-2 text-right font-bold">Avg Rate</th>
                  <th className="px-3 py-2 text-right font-bold bg-[#0F172A]">Required Quantity (Kg)</th>
                  <th className="px-3 py-2 text-right font-bold bg-[#0F172A]">Required Value (₹)</th>
                  <th className="px-3 py-2 text-right font-bold bg-[#0F766E]">RM Stock (Kg)</th>
                  <th className="px-3 py-2 text-right font-bold bg-[#0F766E]">Stock Value (₹)</th>
                  <th className="px-3 py-2 text-right font-bold bg-[#B45309]">WIP (Kg)</th>
                  <th className="px-3 py-2 text-right font-bold bg-[#B45309]">WIP Value (₹)</th>
                  <th className="px-3 py-2 text-right font-bold bg-[#4C1D95]">RM Bought (Kg)</th>
                  <th className="px-3 py-2 text-right font-bold bg-[#4C1D95]">Bought Value (₹)</th>
                  <th className="px-3 py-2 text-right font-bold bg-[#7F1D1D]">Balance Quantity (Kg)</th>
                  <th className="px-3 py-2 text-right font-bold bg-[#7F1D1D]">Balance Value (₹)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E2E8F0]">
                {loading && (
                  <tr><td colSpan={17} className="text-center py-8 text-[#64748B]">Loading…</td></tr>
                )}
                {!loading && filtered.length === 0 && (
                  <tr><td colSpan={17} className="text-center py-12 text-[#94A3B8]">No parts in this window.</td></tr>
                )}
                {filtered.map((r, i) => (
                  <tr key={r.part_id || r.part_no} className={i % 2 === 0 ? "bg-white" : "bg-[#F8FAFC]"} data-testid={`rm-row-${r.part_no}`}>
                    <td className="px-2 py-2 text-center font-mono text-[#64748B]">{r.sr_no}</td>
                    <td className="px-3 py-2 font-mono font-bold text-[#1D2951]">
                      <div>{r.part_no}</div>
                      {r.part_name && (
                        <div className="text-[9px] font-normal text-[#64748B] mt-0.5 truncate max-w-[180px]" title={r.part_name}>{r.part_name}</div>
                      )}
                      {r.company_part_no && r.company_part_no !== r.part_no && (
                        <div className="text-[9px] font-mono text-[#1D4ED8] mt-0.5 truncate max-w-[180px]" title={r.company_part_no_description || r.company_part_no}>
                          ↳ {r.company_part_no}
                          {r.company_part_no_description ? ` · ${r.company_part_no_description}` : ""}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-[11px] text-[#334155] truncate max-w-[180px]" title={r.part_name}>{r.part_name || "—"}</td>
                    <td className="px-3 py-2 text-[11px] text-[#64748B] truncate max-w-[120px]" title={r.customer}>{r.customer || "—"}</td>
                    <td className="px-3 py-2 font-mono text-[10px] text-[#475569]" title={`${r.rm_type || ''} ${r.rm_grade || ''}`}>{r.rm_code}</td>
                    <td className="px-3 py-2 text-right font-mono text-[#64748B]">{f2(r.gross_weight)}</td>
                    <td className="px-3 py-2 text-right font-mono text-[#64748B]">₹{f2(r.avg_rate)}</td>
                    <td className="px-3 py-2 text-right font-mono font-bold">{f2(r.required_qty)}</td>
                    <td className="px-3 py-2 text-right font-mono">{f2(r.required_value)}</td>
                    <td className="px-3 py-2 text-right font-mono text-[#0F766E]">{f2(r.stock_qty)}</td>
                    <td className="px-3 py-2 text-right font-mono text-[#0F766E]">{f2(r.stock_value)}</td>
                    <td className="px-3 py-2 text-right font-mono text-[#B45309]">{f2(r.wip_qty)}</td>
                    <td className="px-3 py-2 text-right font-mono text-[#B45309]">{f2(r.wip_value)}</td>
                    <td className="px-3 py-2 text-right font-mono text-[#7C3AED]">{f2(r.bought_qty)}</td>
                    <td className="px-3 py-2 text-right font-mono text-[#7C3AED]">{f2(r.bought_value)}</td>
                    <td className="px-3 py-2 text-right font-mono font-black text-[#DC2626]">{f2(r.balance_qty)}</td>
                    <td className="px-3 py-2 text-right font-mono font-black text-[#DC2626]">{f2(r.balance_value)}</td>
                  </tr>
                ))}
              </tbody>
              {liveTotals && filtered.length > 0 && (
                <tfoot className="bg-[#0F172A] text-white sticky bottom-0">
                  <tr>
                    <td colSpan={7} className="px-3 py-2 font-bold text-right">TOTAL</td>
                    <td className="px-3 py-2 text-right font-mono font-black">{f2(liveTotals.required_qty)}</td>
                    <td className="px-3 py-2 text-right font-mono font-black">{f2(liveTotals.required_value)}</td>
                    <td className="px-3 py-2 text-right font-mono font-black">{f2(liveTotals.stock_qty)}</td>
                    <td className="px-3 py-2 text-right font-mono font-black">{f2(liveTotals.stock_value)}</td>
                    <td className="px-3 py-2 text-right font-mono font-black">{f2(liveTotals.wip_qty)}</td>
                    <td className="px-3 py-2 text-right font-mono font-black">{f2(liveTotals.wip_value)}</td>
                    <td className="px-3 py-2 text-right font-mono font-black">{f2(liveTotals.bought_qty)}</td>
                    <td className="px-3 py-2 text-right font-mono font-black">{f2(liveTotals.bought_value)}</td>
                    <td className="px-3 py-2 text-right font-mono font-black text-[#FCA5A5]">{f2(liveTotals.balance_qty)}</td>
                    <td className="px-3 py-2 text-right font-mono font-black text-[#FCA5A5]">{f2(liveTotals.balance_value)}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </Card>

        <p className="text-[10px] text-[#94A3B8] px-1">
          One row per part (sorted by highest Balance Procurement Value).
          Required = Sales Register `ordered_qty` × Gross Weight (window-scoped by delivery date).
          WIP = per-part `Balance Production + Balance Issue` from Cutting → last-operation-before-Dispatch × Gross Weight.
          RM Stock &amp; RM Bought are split proportionally across parts sharing the same RM code
          (by each part&apos;s share of Required Quantity).
          Balance Procurement = max(0, Required − (Stock + WIP)) at the weighted-avg purchase rate.
        </p>
      </div>
    </Layout>
  );
}

function TotalTile({ label, qty, value, color, highlight }) {
  return (
    <Card className={`border ${highlight ? 'border-[#FCA5A5] shadow-lg' : 'border-[#E2E8F0]'} overflow-hidden`}
      data-testid={`total-tile-${label.replace(/\s/g, '-').toLowerCase()}`}>
      <div className="px-3 py-1.5" style={{ background: color }}>
        <p className="text-white text-[10px] uppercase tracking-widest font-bold">{label}</p>
      </div>
      {/* Two-row body: Value on top, Kg on bottom. Both equally emphasised
          so the user can read either dimension at a glance. */}
      <div className="divide-y divide-[#E2E8F0]">
        <div className="px-3 py-2 flex items-baseline justify-between gap-2">
          <span className="text-[9px] uppercase tracking-widest font-bold text-[#94A3B8]">Value</span>
          <span className="text-lg font-black text-[#0F172A] font-mono">₹{f2(value)}</span>
        </div>
        <div className="px-3 py-2 flex items-baseline justify-between gap-2 bg-[#F8FAFC]">
          <span className="text-[9px] uppercase tracking-widest font-bold text-[#94A3B8]">Kg</span>
          <span className="text-lg font-black font-mono" style={{ color }}>{f2(qty)}</span>
        </div>
      </div>
    </Card>
  );
}
